import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bot } from "lucide-react";
import { useViewportKind } from "@/hooks/useViewportKind";
import HomeMobile from "@/pages/HomeMobile";
import HomeTablet from "@/pages/HomeTablet";

/**
 * Home — Pandurata base state (the landing).
 *
 * Layout: the scene is a centered square sized min(100vw, 100vh).
 * The W1.png background (natural aspect 2764×1536) is rendered with
 * `background-size: cover`, which crops to a centered 1536×1536 square
 * with NO deformation. The SVG overlay uses viewBox 0 0 1536 1536 with
 * `xMidYMid meet`, so its coordinate system aligns 1:1 with the visible
 * pixels of the cropped image.
 *
 * All 5 navigation labels form a wreath around the central fiddle leaf,
 * with PANDURATA as the crown above. ART DEPT. anchors the top-left
 * corner; LET'S TALK anchors the bottom-right corner.
 */

// --- Curve paths (1536×1536 viewBox) -----------------------------------
// MANIFESTO — vertical along the LEFT EDGE of the central leaf, upper half
const PATH_MANIFESTO =
  "M 470 440 C 350 600, 350 760, 470 920";

// ARCHITECTURE — vertical along the RIGHT EDGE of the central leaf
const PATH_ARCHITECTURE =
  "M 1080 440 C 1200 600, 1200 760, 1080 920";

// REPOSITORY — continues from MANIFESTO, lower half of central leaf's left edge
const PATH_REPOSITORY =
  "M 460 960 C 360 1120, 480 1290, 760 1330";

// LET'S TALK — bottom-right corner, gentle curve up-and-right
const PATH_LETSTALK =
  "M 950 1300 A 440 440 0 0 1 1510 1230";

// ART DEPT. — top-left corner, small horizontal arc (breathing room from wordmark)
const PATH_ARTDEPT =
  "M 30 580 A 380 380 0 0 1 420 420";

const Home = () => {
  const navigateTo = useNavigate();
  const navigate = (path) => navigateTo(path);
  const viewportKind = useViewportKind();

  if (viewportKind === "mobile") {
    return <HomeMobile />;
  }
  if (viewportKind === "tablet") {
    return <HomeTablet />;
  }

  const onKey = (e, path) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      navigate(path);
    }
  };

  return (
    <main className="home" data-testid="home-page">
      <div className="home__scene" data-testid="home-scene">
        <h1 className="home__wordmark" data-testid="wordmark-pandurata">
          PANDURATA
        </h1>

        <svg
          className="home__svg"
          viewBox="0 0 1536 1536"
          preserveAspectRatio="xMidYMid meet"
          xmlns="http://www.w3.org/2000/svg"
          aria-label="Pandurata navigation"
        >
          <defs>
            <path id="curve-manifesto" d={PATH_MANIFESTO} />
            <path id="curve-architecture" d={PATH_ARCHITECTURE} />
            <path id="curve-repository" d={PATH_REPOSITORY} />
            <path id="curve-letstalk" d={PATH_LETSTALK} />
            <path id="curve-artdept" d={PATH_ARTDEPT} />
          </defs>

          {/* MANIFESTO — left edge of central leaf, upper half */}
          <text
            className="home__label"
            role="link"
            tabIndex={0}
            aria-label="Manifesto"
            onClick={() => navigate("/manifesto")}
            onKeyDown={(e) => onKey(e, "/manifesto")}
            data-testid="label-manifesto"
          >
            <textPath href="#curve-manifesto" startOffset="50%" textAnchor="middle">
              MANIFESTO
            </textPath>
          </text>

          {/* ARCHITECTURE — right edge of central leaf */}
          <text
            className="home__label"
            role="link"
            tabIndex={0}
            aria-label="Architecture"
            onClick={() => navigate("/architecture")}
            onKeyDown={(e) => onKey(e, "/architecture")}
            data-testid="label-architecture"
          >
            <textPath href="#curve-architecture" startOffset="50%" textAnchor="middle">
              ARCHITECTURE
            </textPath>
          </text>

          {/* REPOSITORY — bottom of central leaf */}
          <text
            className="home__label"
            role="link"
            tabIndex={0}
            aria-label="Repository"
            onClick={() => navigate("/repository")}
            onKeyDown={(e) => onKey(e, "/repository")}
            data-testid="label-repository"
          >
            <textPath href="#curve-repository" startOffset="50%" textAnchor="middle">
              REPOSITORY
            </textPath>
          </text>

          {/* LET'S TALK — bottom-right corner */}
          <text
            className="home__label"
            role="link"
            tabIndex={0}
            aria-label="Let's Talk"
            onClick={() => navigate("/lets-talk")}
            onKeyDown={(e) => onKey(e, "/lets-talk")}
            data-testid="label-letstalk"
          >
            <textPath href="#curve-letstalk" startOffset="50%" textAnchor="middle">
              LET&apos;S TALK
            </textPath>
          </text>

          {/* ART DEPT. — top-left corner */}
          <text
            className="home__label"
            role="link"
            tabIndex={0}
            aria-label="Art Dept"
            onClick={() => navigate("/art-dept")}
            onKeyDown={(e) => onKey(e, "/art-dept")}
            data-testid="label-artdept"
          >
            <textPath href="#curve-artdept" startOffset="50%" textAnchor="middle">
              ART DEPT.
            </textPath>
          </text>
        </svg>
      </div>

      <div className="home__mark" data-testid="footer-mark">
        EST · BOTANY MEETS BRUTALISM
      </div>

      <Link
        to="/ai"
        className="ai-toggle"
        aria-label="Switch to AI-readable mode"
        data-testid="ai-toggle"
      >
        <Bot size={13} strokeWidth={1.7} />
        <span>AI READABLE</span>
      </Link>
    </main>
  );
};

export default Home;
